import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { format, parse } from 'date-fns';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { URLParameters } from '@/app/common/urlParameters';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import getSearchParams from '@/app/components/search/common/getSearchParams';
import { createDetailPageUserActionPayload } from '@/app/common/starTrack/events/detailPageUserAction.ts';
import { trackRoomShowDetails } from '../trackInteractionEvents';
import { useCuriosity } from '../CuriosityProvider';
import { useBrowserBackButton } from '../hooks/useBrowserBackButton';
import { FailedRoomPhotosProvider } from '../hooks/useLoadableImages';
import { useOffersContext } from '../OffersProvider';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { getCancellationPolicy, getRoomCancellationOptions } from './RoomPanelCommon';

type RoomPanelModalContextType = {
  closeModal: () => void;
  isOpen: boolean | undefined;
  selectedRoom: RoomType | undefined;
  // The meal plan the opener was displaying (e.g. the board class selected in the rooms list), so the modal
  // can show that meal plan's cancellation options instead of the page-load default.
  preselectedMealPlan: MealPlanType | undefined;
};

type RoomPanelOpenModalContextType = {
  openModal: (room: RoomType | undefined, mealPlan?: MealPlanType) => void;
};

const RoomPanelModalContext = createContext<RoomPanelModalContextType | undefined>(undefined);
const RoomPanelOpenModalContext = createContext<RoomPanelOpenModalContextType | undefined>(undefined);

export function RoomPanelModalProvider(props: { children: React.ReactNode; preSelectedRoom?: RoomType | undefined }) {
  const [isOpen, setIsOpen] = useState<boolean | undefined>(undefined);
  const [selectedRoom, setSelectedRoom] = useState<RoomType | undefined>(props.preSelectedRoom);
  const [preselectedMealPlan, setPreselectedMealPlan] = useState<MealPlanType | undefined>(undefined);
  const { sendCuriosityClickEvent, sendCuriosityRoomShownEvent } = useCuriosity();
  const { localization } = usePageWrapperData();
  const urlSearchParams = useUrlSearchParams();
  const { isTrain, children: numberOfChildren, infants, nights } = urlSearchParams;
  const { offersData } = useOffersContext();
  const { starTrack } = useCommonClient();
  const searchParams = getSearchParams();
  const searchId = searchParams[URLParameters.SEARCH_ID];
  const initialParams = useInitialParams();
  const { productDetailKey } = initialParams;

  const openModal = useCallback((room: RoomType | undefined, mealPlan?: MealPlanType) => {
    setIsOpen(true);
    setSelectedRoom(room);
    setPreselectedMealPlan(mealPlan);
    sendCuriosityClickEvent({
      event_name: 'click_show_room_details',
      event_context: 'room_panel',
      event_value: 'to_expand_it',
    });
    starTrack?.detailPageUserAction(
      createDetailPageUserActionPayload({
        searchId: searchId || '',
        productDetailKey,
        action: 'ROOM_INFO',
        roomId: room?.roomId,
      })
    );
    const groupedCancellationPolicies = getRoomCancellationOptions(room?.configurations || []);
    const cancellationKey = getCancellationPolicy(room?.configurations?.[0]?.cancellationPolicy || { type: 'UNKNOWN' });
    const mealPlansPerCancellationPolicy =
      groupedCancellationPolicies[cancellationKey].deferredPaymentDepositPerMealPlan.length;
    sendCuriosityRoomShownEvent({
      people_child: numberOfChildren,
      people_infant: infants,
      checkin_date: offersData?.checkin ? format(parse(offersData.checkin, 'yyyyMMdd', new Date()), 'yyyy-MM-dd') : '', // Format YYYY-MM-DD
      checkout_date: offersData?.checkout
        ? format(parse(offersData.checkout, 'yyyyMMdd', new Date()), 'yyyy-MM-dd')
        : '', // Format YYYY-MM-DD
      nights: offersData?.nights || nights,
      product_type: isTrain ? 'TH' : 'DP',
      cheapest_price: offersData?.cheapestFilteredMealPlan?.finalTotalPrice,
      cheapest_price_currency: localization.currency,
      room_id: room?.roomId,
      room_has_amenities: (room?.amenities.length || 0) > 0 ? 1 : 0,
      room_has_description: (room?.descriptions.length || 0) > 0 ? 1 : 0,
      room_has_photo: (room?.images.length || 0) > 0 ? 1 : 0,
      room_options: `${Object.keys(groupedCancellationPolicies).length},${mealPlansPerCancellationPolicy}`,
      room_position: offersData?.rooms?.findIndex((currentRoom) => currentRoom.roomOfferId === room?.roomOfferId) || 0,
      is_cheapest_room: offersData?.rooms?.[0]?.roomOfferId === room?.roomOfferId ? 1 : 0,
    });
    trackRoomShowDetails(true);
  }, []);

  const closeModal = () => {
    setIsOpen(false);
    sendCuriosityClickEvent({
      event_name: 'click_show_room_details',
      event_context: 'room_panel',
      event_value: 'to_collapse_it',
    });
    trackRoomShowDetails(false);
  };

  useBrowserBackButton({
    condition: isOpen,
    modalId: selectedRoom?.roomOfferId ? `room-details-${selectedRoom?.roomOfferId}` : 'room-details',
    onClose: closeModal,
    onOpen: () => openModal(selectedRoom, preselectedMealPlan),
  });

  useEffect(() => {
    if (props.preSelectedRoom) {
      setSelectedRoom(props.preSelectedRoom);
      setIsOpen(true);
    }
  }, [props.preSelectedRoom]);

  const roomsPanelModalMemoizedValue = useMemo(
    () => ({
      closeModal,
      isOpen,
      selectedRoom,
      preselectedMealPlan,
    }),
    [closeModal, isOpen, selectedRoom, preselectedMealPlan]
  );

  const roomsPanelOpenModalMemoizedValue = useMemo(
    () => ({
      openModal,
    }),
    [openModal]
  );

  return (
    <RoomPanelModalContext.Provider value={roomsPanelModalMemoizedValue}>
      <RoomPanelOpenModalContext.Provider value={roomsPanelOpenModalMemoizedValue}>
        {/* MPH-1321: the room cards and the room details galleries share the photos that failed to load. */}
        <FailedRoomPhotosProvider>{props.children}</FailedRoomPhotosProvider>
      </RoomPanelOpenModalContext.Provider>
    </RoomPanelModalContext.Provider>
  );
}

export function useRoomPanelModal() {
  const context = useContext(RoomPanelModalContext);

  if (context === undefined) {
    throw new Error('useRoomPanelModal must be within a RoomPanelModalProvider');
  }

  return context;
}

export function useRoomPanelOpenModal() {
  const context = useContext(RoomPanelOpenModalContext);

  if (context === undefined) {
    throw new Error('useRoomPanelOpenModal must be within a RoomPanelOpenModalProvider');
  }

  return context;
}
